import React from 'react';
import {
    Box,
    ListPrice,
    Pricing,
    ShortTaxMessage,
    SkeletonText,
    SkeletonTextLine,
    StyleFontSize,
    StyleFontWeight,
} from '@vp/swan';
import { __ } from '@99designs/i18n';
import { usePricingContextIfProvided } from '../../hooks/usePricingContextIfProvided';
import { useVatMessaging } from '../../hooks/useVatMessaging';
import { PriceType } from '../../types';
import { priceFromCents } from '../../utils/priceFromCents';

export type PriceProps = {
    price?: PriceType;
    loading?: boolean;
    subtleTaxMessage?: boolean;
    stacked?: boolean;
    fontSize?: StyleFontSize;
    fontWeight?: StyleFontWeight;
    skeletonWidth?: 'standard' | 'narrow';
};

export function Price({
    price,
    loading = false,
    subtleTaxMessage = false,
    stacked = false,
    fontSize,
    fontWeight,
    skeletonWidth,
}: PriceProps): React.ReactNode {
    if (!price && !loading) {
        return null;
    }

    return (
        <Box as="span" data-testid="price-container" aria-live="polite" aria-busy={loading}>
            <PriceContent
                price={price}
                loading={loading}
                subtleTaxMessage={subtleTaxMessage}
                stacked={stacked}
                fontSize={fontSize}
                fontWeight={fontWeight}
                skeletonWidth={skeletonWidth}
            />
        </Box>
    );
}

type PriceContentProps = {
    price: PriceType | undefined;
    loading: boolean;
    subtleTaxMessage: boolean;
    stacked: boolean;
    fontSize?: StyleFontSize;
    fontWeight?: StyleFontWeight;
    skeletonWidth?: 'standard' | 'narrow';
};

function PriceContent({
    price,
    loading,
    subtleTaxMessage,
    stacked,
    fontSize,
    fontWeight,
    skeletonWidth = 'narrow',
}: PriceContentProps) {
    const pricingContext = usePricingContextIfProvided();
    const vatMessage = useVatMessaging();

    if (loading) {
        return (
            <SkeletonText data-testid="price-skeleton">
                <SkeletonTextLine width={skeletonWidth} />
            </SkeletonText>
        );
    }

    if (!price) {
        return null;
    }

    const amount = pricingContext?.vatInclusive ? price.listPrice.taxed : price.listPrice.untaxed;
    const formattedPrice = priceFromCents({ amount, currency: price.currency });

    const stackedStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
    };

    return (
        <Pricing
            fontSize={fontSize}
            fontWeight={fontWeight}
            style={stacked ? stackedStyle : undefined}
        >
            <ListPrice>
                {price.isStartingPrice
                    ? __('From {{price}}', {
                          price: formattedPrice,
                          _context:
                              'Price shown for a product that reflects its lowest-priced option, e.g. "From $10.00"',
                      })
                    : formattedPrice}
            </ListPrice>
            {vatMessage && (
                <ShortTaxMessage
                    fontSkin={subtleTaxMessage ? 'body-standard' : undefined}
                    textColor={subtleTaxMessage ? 'subtle' : undefined}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {` (${vatMessage})`}
                </ShortTaxMessage>
            )}
        </Pricing>
    );
}
